/** Display formatting helpers. Keep pure — used across pages. */
import { daysBetween, parseDate } from '@shared/dates';

export { daysSince, parseDate } from '@shared/dates';
export { fmtBytes } from '@shared/format';

/**
 * `toFixed` that never prints a minus zero. Scales report signed noise at rest (-0.03), and `(-0.03).toFixed(1)` is
 * "-0.0"; anything that rounds to zero prints as "0.0" instead. Real negatives keep their sign, as a true minus
 * (U+2212, the width of a digit in the mono read-outs) rather than a hyphen. For display only: never parse it back.
 */
export function fmtFixed(v: number, decimals: number): string {
  return (Math.abs(v) < 0.5 * 10 ** -decimals ? 0 : v).toFixed(decimals).replace('-', '\u2212');
}

export function fmtWeight(g: number | null | undefined, decimals = 1, unit = 'g'): string {
  if (g == null || !Number.isFinite(g)) return '–';
  return `${fmtFixed(g, decimals)}${unit ? ` ${unit}` : ''}`;
}

/**
 * A ratio (yield / coffee) as a read-out: "1:2.0", "1:16.5". Always one decimal, wherever a ratio is shown, so
 * measured and recipe ratios line up. Inputs take the number after a "1:" prefix (see ratioDigits).
 */
export function fmtRatio(ratio: number | null | undefined): string {
  if (ratio == null || !Number.isFinite(ratio)) return '–';
  return `1:${ratio.toFixed(1)}`;
}

/** A ratio's number in a "1:" input: one decimal, two when the second one counts ("2.0", "2.25", "16.67"). */
export function ratioDigits(ratio: number): string {
  const two = ratio.toFixed(2);
  return two.endsWith('0') ? ratio.toFixed(1) : two;
}

/** Espresso:milk as "1:3.5", "1:2.0": one decimal, like every other ratio (fmtRatio). */
export function fmtMilkRatio(ratio: number | null | undefined): string {
  return fmtRatio(ratio);
}

/** Seconds -> "27.4s" (under a minute) or "3:05" (a minute or more, rounded to whole seconds). */
export function fmtDuration(s: number | null | undefined): string {
  if (s == null || !Number.isFinite(s)) return '–';
  // 59.95 would print as "60.0s"; from there on use m:ss.
  if (s < 59.95) return `${s.toFixed(1)}s`;
  // Round the total before splitting so 179.5 s becomes "3:00", not "2:60".
  const t = Math.round(s);
  const m = Math.floor(t / 60);
  return `${m}:${String(t % 60).padStart(2, '0')}`;
}

/** Milliseconds -> "00:27.4" stopwatch display. */
export function fmtStopwatch(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '00:00.0';
  const totalTenths = Math.floor(ms / 100);
  const tenths = totalTenths % 10;
  const totalSec = Math.floor(totalTenths / 10);
  const sec = totalSec % 60;
  const min = Math.floor(totalSec / 60);
  return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}.${tenths}`;
}

// ---------------------------------------------------------------------------------------------------------------
// Temperature. Stored, sent and validated in °C; °F exists only at display and input time (Settings › Units).
// ---------------------------------------------------------------------------------------------------------------

export type TemperatureUnit = 'c' | 'f';

/** Valid temperatures, in °C (mirrors the schema's 0–100). */
export const TEMP_MIN_C = 0;
export const TEMP_MAX_C = 100;

export function cToF(c: number): number {
  return (c * 9) / 5 + 32;
}

export function fToC(f: number): number {
  return ((f - 32) * 5) / 9;
}

/** "°C" or "°F". */
export function tempUnitLabel(unit: TemperatureUnit): string {
  return unit === 'f' ? '°F' : '°C';
}

/** The number shown for a stored °C value: °C to 0.1 (no trailing .0), °F to a whole degree. */
export function tempToDisplay(c: number, unit: TemperatureUnit): number {
  return unit === 'f' ? Math.round(cToF(c)) : roundTo(c, 0.1);
}

/**
 * The °C to store for a number entered in `unit`. °C passes through unchanged. °F is rounded to a whole degree, and
 * stored as the °C with the fewest decimals (whole, else one) that shows as that °F again, so 199 °F is 93 °C, not
 * 92.78. `keepC` (the value the field started with) wins when it already shows as the entered °F, so opening 92.5 °C
 * (199 °F) and typing 199 again keeps 92.5.
 */
export function tempFromInput(value: number, unit: TemperatureUnit, keepC?: number | null): number {
  if (unit === 'c') return value;
  const f = Math.round(value);
  if (keepC != null && Number.isFinite(keepC) && Math.round(cToF(keepC)) === f) return keepC;
  const exact = fToC(f);
  const whole = Math.round(exact);
  if (Math.round(cToF(whole)) === f) return whole;
  // 0.1 °C is 0.18 °F, so the nearest tenth always shows as f again.
  return roundTo(exact, 0.1);
}

/** The valid range in `unit`: 0–100 °C or 32–212 °F. */
export function tempRange(unit: TemperatureUnit): { min: number; max: number } {
  return unit === 'f' ? { min: cToF(TEMP_MIN_C), max: cToF(TEMP_MAX_C) } : { min: TEMP_MIN_C, max: TEMP_MAX_C };
}

/** "0–100 °C" or "32–212 °F", for validation messages. */
export function fmtTempRange(unit: TemperatureUnit): string {
  const { min, max } = tempRange(unit);
  return `${min}–${max} ${tempUnitLabel(unit)}`;
}

/** Stored °C -> "93 °C", "92.5 °C" or "199 °F". */
export function fmtTemp(c: number | null | undefined, unit: TemperatureUnit = 'c'): string {
  if (c == null || !Number.isFinite(c)) return '–';
  return `${tempToDisplay(c, unit)} ${tempUnitLabel(unit)}`;
}

// ---------------------------------------------------------------------------------------------------------------
// Bag, stock and milk weights. Stored, sent and validated in grams; ounces exist only at display and input time
// (Settings › Units). Dose, yield, flow, live readouts and vessels are always grams and use fmtWeight above.
// Ounces are weight ounces (avoirdupois), not fluid ounces: milk is weighed on the scale.
// ---------------------------------------------------------------------------------------------------------------

export type WeightUnit = 'g' | 'oz';

/** Grams in one (avoirdupois) ounce. */
export const G_PER_OZ = 28.349523125;

export function gToOz(g: number): number {
  return g / G_PER_OZ;
}

export function ozToG(oz: number): number {
  return oz * G_PER_OZ;
}

/** "g" or "oz". */
export function weightUnitLabel(unit: WeightUnit): string {
  return unit === 'oz' ? 'oz' : 'g';
}

/** The number shown for stored grams: grams unchanged, ounces to 0.1. */
export function weightToDisplay(g: number, unit: WeightUnit): number {
  return unit === 'oz' ? roundTo(gToOz(g), 0.1) : g;
}

/**
 * The grams to store for a number entered in `unit`. Grams pass through unchanged. Ounces are rounded to 0.1 and
 * stored as the whole grams that show as that oz again (0.1 oz is 2.8 g, so there always is one), so 12 oz is 340 g,
 * not 340.19. `keepG` (the value the field started with) wins when it already shows as the entered oz, so opening
 * 341 g (12.0 oz) and typing 12 again keeps 341.
 */
export function weightFromInput(value: number, unit: WeightUnit, keepG?: number | null): number {
  if (unit === 'g') return value;
  const oz = roundTo(value, 0.1);
  if (keepG != null && Number.isFinite(keepG) && weightToDisplay(keepG, 'oz') === oz) return keepG;
  const exact = ozToG(oz);
  const whole = Math.round(exact);
  if (weightToDisplay(whole, 'oz') === oz) return whole;
  return roundTo(exact, 0.1);
}

/**
 * A grams bound (an input's min or max) in `unit`. Ounces round inwards to 0.1, so a value clamped to it never
 * converts to grams outside the grams range: at most 1000 g is at most 35.2 oz.
 */
export function weightBoundToDisplay(g: number, unit: WeightUnit, edge: 'min' | 'max'): number {
  if (unit === 'g') return g;
  const tenths = roundTo(gToOz(g) * 10, 1e-6);
  return (edge === 'min' ? Math.ceil(tenths) : Math.floor(tenths)) / 10;
}

/**
 * Stored grams -> "340 g" (`gDecimals`, default whole grams) or "12.0 oz" (always one decimal). Amounts of a pound or
 * more stay in ounces ("16.0 oz", not "1 lb"), so a stock reads against its bag and matches what the inputs take.
 */
export function fmtWeightIn(g: number | null | undefined, unit: WeightUnit, gDecimals = 0): string {
  if (unit === 'g') return fmtWeight(g, gDecimals);
  if (g == null || !Number.isFinite(g)) return '–';
  return fmtWeight(weightToDisplay(g, 'oz'), 1, 'oz');
}

export function fmtFlow(gs: number | null | undefined): string {
  if (gs == null || !Number.isFinite(gs)) return '–';
  return `${fmtFixed(gs, 1)} g/s`;
}

const dateFmt = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
const dateTimeFmt = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const timeFmt = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' });

/** 'YYYY-MM-DD' or ISO timestamp -> "12 Oct 2026" */
export function fmtDate(value: string | null | undefined): string {
  if (!value) return '–';
  const d = parseDate(value);
  return d ? dateFmt.format(d) : '–';
}

export function fmtDateTime(value: string | null | undefined): string {
  if (!value) return '–';
  const d = parseDate(value);
  return d ? dateTimeFmt.format(d) : '–';
}

export function fmtTime(value: string | null | undefined): string {
  if (!value) return '–';
  const d = parseDate(value);
  return d ? timeFmt.format(d) : '–';
}

/** "just now", "5 min ago", "3 h ago", "yesterday", "4 days ago", else date. */
export function fmtRelative(value: string | null | undefined, now: Date = new Date()): string {
  if (!value) return '–';
  const d = parseDate(value);
  if (!d) return '–';
  const diffS = (now.getTime() - d.getTime()) / 1000;
  if (diffS < 45) return 'just now';
  if (diffS < 3600) return `${Math.round(diffS / 60)} min ago`;
  if (diffS < 86400 && d.getDate() === now.getDate()) return `${Math.round(diffS / 3600)} h ago`;
  const days = daysBetween(d, now);
  if (days === 1) return 'yesterday';
  if (days < 14) return `${days} days ago`;
  return fmtDate(value);
}

/** Today's local date as 'YYYY-MM-DD'. */
export function todayIso(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/** Round to a step (e.g. 0.1) avoiding float noise. */
export function roundTo(value: number, step = 0.1): number {
  const inv = 1 / step;
  return Math.round(value * inv) / inv;
}

/** Parse a user-entered number ("18,5" or "18.5"); returns null for empty/invalid. */
export function parseNum(input: string): number | null {
  const t = input.trim().replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}
